/**
 * Where a learner got to, shown at once (13 Oct 2026).
 *
 * The server is the record: every move, narration part and answer is an event, and progress is
 * rebuilt from them. But a course list opened the instant a learner leaves a course can be read
 * before the last save lands, and showed the place from before this sitting. So leaving notes the
 * place here, in memory, and every course list (through orgApi.myLearning) shows the note over the
 * server's answer — the PLACE only, with `progressPending` set so the page can say the progress is
 * still updating (the percentage is the server's, worked out from the narration heard; it is never
 * guessed here). Once the server has caught up the note is dropped and its figures stand.
 *
 * Never stored anywhere: a reload reads the server alone. Caught up = the server's last activity
 * is at least the time of the last event this sitting sent.
 */
import type { LearningItem } from './orgApi'

interface Note {
  currentSlide: number
  furthestSlide: number
  slideCount: number
  /** `at` of the last event the sitting sent — the server is current once its lastActiveAt reaches it. */
  lastEventAt: string
  /** A new attempt was begun on this page (Reattempt / Try again, or a language switch): a finished
   *  course is being taken again, though the server may not know yet (13 Oct 2026). */
  newAttempt?: boolean
}
const notes = new Map<string, Note>()

export function noteLearnPlace(launchId: string, note: Note): void {
  if (!note.lastEventAt) return
  notes.set(launchId, note)
}
/** Forget a note — a reattempt or language switch starts a fresh attempt the note knows nothing of. */
export function forgetLearnPlace(launchId: string): void {
  notes.delete(launchId)
}

/** The server's items with any newer local place laid over them. */
export function withLearnPlaces(items: LearningItem[]): LearningItem[] {
  if (!notes.size) return items
  return items.map(item => {
    const n = notes.get(item.launchId)
    if (!n) return item
    const p = item.progress
    if (p?.lastActiveAt && Date.parse(p.lastActiveAt) >= Date.parse(n.lastEventAt)) { notes.delete(item.launchId); return item }
    // Never past the last slide. The course's own count is the authority — the note's can be 0 when it
    // was written before the page had the course (13 Oct 2026: “slide 17 of 16”).
    const count = (item.versions.find(v => v.courseId === p?.versionCourseId) ?? item.versions[0])?.slideCount || n.slideCount
    const at = count ? Math.min(n.currentSlide, count - 1) : n.currentSlide
    // The place is exact; the percentage is the server's to work out (from the narration heard),
    // so it is left as it was and the item says it is catching up — no guessed figure (13 Oct 2026).
    if (p?.retake) return { ...item, progressPending: true, progress: { ...p, retake: { ...p.retake, currentSlide: at, furthestSlide: Math.max(p.retake.furthestSlide ?? 0, n.furthestSlide) } } }
    // Finished, and taken again here before the server heard of it: show it as being taken again —
    // the finished result stays underneath, as it does once the server catches up.
    if (p?.status === 'completed') return n.newAttempt ? { ...item, progressPending: true, progress: { ...p, retake: { currentSlide: at, percent: 0, furthestSlide: n.furthestSlide } } } : item
    return {
      ...item,
      // A new attempt begun here after the quiz was failed: no longer “Quiz not passed” (D1).
      ...(n.newAttempt ? { quizNotPassed: false } : {}),
      progressPending: true,
      status: item.status === 'not_started' ? 'in_progress' : item.status,
      progress: {
        ...(p ?? { status: 'in_progress', language: null, versionCourseId: null, percent: 0, score: null, passed: null, completedAt: null, lastActiveAt: null }),
        status: 'in_progress', currentSlide: at, furthestSlide: Math.max(p?.furthestSlide ?? 0, n.furthestSlide),
      },
    }
  })
}
